/**
 * Undo/Redo system — Core types and ActionUndoManager (Fas 14, §28).
 *
 * Two undo systems coexist:
 * 1. Action Undo (task mutations) — this module
 * 2. Editor Undo (TipTap/Yjs) — handled by Y.UndoManager
 *
 * Cmd+Z context router dispatches to the correct system based on focus.
 *
 * Design decisions (per POC 38 research):
 * - Stack depth: 30 (Things 3-level)
 * - No redo for now (Things 3 pattern — simpler, less confusing)
 * - Batch operations = single undo step
 * - Stack cleared on view navigation
 * - Undo creates reverse mutation (syncs via Dexie Cloud)
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type UndoEntryType = "create" | "update" | "delete" | "batch"

export interface UndoEntry {
  /** Unique ID for this entry */
  id: string
  /** When the action was performed */
  timestamp: number
  /** Human-readable description (for toast/debug) */
  description: string
  /** Category of the action */
  type: UndoEntryType
  /** Reverse the action */
  undo: () => Promise<void>
  /** Re-apply the action (for future redo support) */
  redo: () => Promise<void>
}

// ---------------------------------------------------------------------------
// ActionUndoManager
// ---------------------------------------------------------------------------

const MAX_STACK_DEPTH = 30

export class ActionUndoManager {
  private undoStack: UndoEntry[] = []
  private redoStack: UndoEntry[] = []

  /** Push a new undoable action. Clears redo stack. */
  push(entry: UndoEntry): void {
    this.undoStack.push(entry)
    if (this.undoStack.length > MAX_STACK_DEPTH) {
      this.undoStack.shift() // Remove oldest
    }
    this.redoStack = [] // Clear redo on new action
  }

  /** Undo the most recent action. Returns the entry if successful. */
  async undo(): Promise<UndoEntry | null> {
    const entry = this.undoStack.pop()
    if (!entry) return null

    try {
      await entry.undo()
      this.redoStack.push(entry)
      return entry
    } catch (error) {
      console.error("[undo] Failed to undo action:", entry.description, error)
      // Put it back on the stack since undo failed
      this.undoStack.push(entry)
      return null
    }
  }

  /** Redo the most recently undone action. */
  async redo(): Promise<UndoEntry | null> {
    const entry = this.redoStack.pop()
    if (!entry) return null

    try {
      await entry.redo()
      this.undoStack.push(entry)
      return entry
    } catch (error) {
      console.error("[undo] Failed to redo action:", entry.description, error)
      this.redoStack.push(entry)
      return null
    }
  }

  /** Clear both stacks (e.g., on navigation). */
  clear(): void {
    this.undoStack = []
    this.redoStack = []
  }

  get canUndo(): boolean {
    return this.undoStack.length > 0
  }

  get canRedo(): boolean {
    return this.redoStack.length > 0
  }

  get undoCount(): number {
    return this.undoStack.length
  }

  get redoCount(): number {
    return this.redoStack.length
  }

  /** Peek at the most recent undoable action (for toast description). */
  get lastAction(): UndoEntry | undefined {
    return this.undoStack[this.undoStack.length - 1]
  }
}
